// ============================================
// Elite Gymnastics - Main App Router
// ============================================

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Layouts
import PublicLayout from './components/layout/PublicLayout';
import AdminLayout from './components/layout/AdminLayout';
import ClientLayout from './components/layout/ClientLayout';

// Public Pages
import Home from './pages/public/Home';
import About from './pages/public/About';
import Classes from './pages/public/Classes';
import Gallery from './pages/public/Gallery';
import Blog from './pages/public/Blog';
import BlogPost from './pages/public/BlogPost';
import Pricing from './pages/public/Pricing';
import Team from './pages/public/Team';
import Contact from './pages/public/Contact';
import Locations from './pages/public/Locations';
import Uniforms from './pages/public/Uniforms';
import Membership from './pages/public/Membership';
import PageRenderer from './pages/public/PageRenderer';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminBookings from './pages/admin/Bookings';
import AdminClasses from './pages/admin/Classes';
import AdminCalendar from './pages/admin/Calendar';
import AdminPages from './pages/admin/Pages';
import AdminBlog from './pages/admin/Blog';
import AdminAIWriter from './pages/admin/AIWriter';
import AdminMedia from './pages/admin/Media';
import AdminSettings from './pages/admin/Settings';
import AdminReports from './pages/admin/Reports';

// Client Pages
import ClientDashboard from './pages/client/Dashboard';
import MyBookings from './pages/client/MyBookings';
import MyInvoices from './pages/client/MyInvoices';
import MyProgress from './pages/client/MyProgress';
import Messages from './pages/client/Messages';
import Profile from './pages/client/Profile';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

// Admin Route Component
function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'OWNER_ADMIN', 'STAFF']}>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
}

// Client Route Component
function ClientRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <ClientLayout>{children}</ClientLayout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <Routes>
      {/* Public Routes with Layout */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/classes" element={<Classes />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/team" element={<Team />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/uniforms" element={<Uniforms />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/:slug" element={<PageRenderer />} />
      </Route>
      
      {/* Auth Routes - Short URLs */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      
      {/* Auth Routes */}
      <Route path="/auth/login" element={<Login />} />
      <Route path="/auth/register" element={<Register />} />
      <Route path="/auth/verify" element={<VerifyEmail />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
      <Route path="/admin/classes" element={<AdminRoute><AdminClasses /></AdminRoute>} />
      <Route path="/admin/calendar" element={<AdminRoute><AdminCalendar /></AdminRoute>} />
      <Route path="/admin/pages" element={<AdminRoute><AdminPages /></AdminRoute>} />
      <Route path="/admin/pages/:id/edit" element={<AdminRoute><AdminPages /></AdminRoute>} />
      <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
      <Route path="/admin/ai-writer" element={<AdminRoute><AdminAIWriter /></AdminRoute>} />
      <Route path="/admin/media" element={<AdminRoute><AdminMedia /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
      
      {/* Client Routes */}
      <Route path="/client" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
      <Route path="/client/dashboard" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
      <Route path="/client/bookings" element={<ClientRoute><MyBookings /></ClientRoute>} />
      <Route path="/client/invoices" element={<ClientRoute><MyInvoices /></ClientRoute>} />
      <Route path="/client/progress" element={<ClientRoute><MyProgress /></ClientRoute>} />
      <Route path="/client/messages" element={<ClientRoute><Messages /></ClientRoute>} />
      <Route path="/client/profile" element={<ClientRoute><Profile /></ClientRoute>} />
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;